import { hexToRgba } from '../utils'

const palette = {
	gray: {
		1: '#F4F4F6',
		2: '#EFEFF2',
		3: '#E3E3E7',
		4: '#CACFD9',
		5: '#89909D',
		6: '#626570',
		7: '#434651',
		8: '#373A44',
		9: '#202227',
	},
	arctic: {
		1: '#E7FFFF',
		2: '#BEF8EC',
		3: '#94F1DA',
		4: '#6BEAC7',
		5: '#42E3B4',
		6: '#31C2A7',
		7: '#21A19A',
		8: '#107F8C',
		9: '#005E7F',
	},
	emerald: {
		1: '#EEF7F2',
		2: '#CFEDD8',
		3: '#97DDAC',
		4: '#6CD89F',
		5: '#21BA72',
		6: '#1EA666',
		7: '#0C8151',
		8: '#036541',
		9: '#02332A',
	},
	pink: {
		1: '#FFE5F3',
		2: '#FEB3D2',
		3: '#FE87B7',
		4: '#FF3C8A',
		5: '#FF0066',
		6: '#DA005E',
		7: '#B40057',
		8: '#8F0051',
		9: '#69004A',
	},
	blue: {
		1: '#E8F5FF',
		2: '#BBE0FE',
		3: '#89C7FF',
		4: '#5AB0FF',
		5: '#0087CD',
		6: '#1164C0',
		7: '#184EA1',
		8: '#203883',
		9: '#13224E',
	},
	gold: {
		1: '#FFF8E5',
		2: '#FFE6B6',
		3: '#FFD685',
		4: '#FFCC47',
		5: '#FAAD14',
		6: '#DD6F00',
		7: '#BD4F00',
		8: '#A53B00',
		9: '#8F2B00',
	},
}
const colors = {
	white: '#FFFFFF',
	sun: '#FFC700',
	crimson: '#FF004D',
	magenta: '#DA009D',
	violet: '#A08AFF',
	coop: '#107F8C',
	premium: '#CD0C97',
	overlay: hexToRgba('#000000', 0.5),
	selection: hexToRgba(palette.emerald[5], 0.16),
	dropdownShadow: hexToRgba('#000000', 0.15),
	extraBackground1: '#453F6B',
	extraBackground2: '#2A2449',
	extraBackground3: '#1B1638',
	colorMainBg: '#f3f3f4',
	warningColor: '#ffcc00',
}

export type PaletteNames = keyof typeof palette
export type ColorNames = keyof typeof colors
export type Colors = ColorNames & PaletteNames

export type StyledThemeFn = ({ theme }: WithTheme) => string

export function themeColor(name: ColorNames): StyledThemeFn
export function themeColor(palette: PaletteNames, index: number): StyledThemeFn
export function themeColor(name: ColorNames | PaletteNames, index?: number) {
	return ({ theme }: WithTheme) => (index ? theme.colors.palette[name][index] : theme.colors[name])
}

export function rgba(name: ColorNames, alpha: number): StyledThemeFn
export function rgba(palette: PaletteNames, index: number, alpha: number): StyledThemeFn
export function rgba(name: ColorNames | PaletteNames, index: number, alpha?: number) {
	return ({ theme }: WithTheme) =>
		hexToRgba(alpha ? theme.colors.palette[name][index] : theme.colors[name], alpha ?? index)
}

type PaletteIndex = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'
export type PaletteColorNames = `${PaletteNames}${PaletteIndex}`
export function themeColorByColorName(color: PaletteColorNames) {
	return ({ theme }: WithTheme) => theme.colors.palette[color.slice(0, -1)][color.slice(-1)]
}

const fontFamily = {
	SBSansText: "'SBSansText', 'Open Sans', sans-serif",
	SBSansInterface: "'SBSansInterface', 'Open Sans', sans-serif",
}
export type FontFamily = keyof typeof fontFamily

export function themeFontFamily(fontFamily: FontFamily) {
	return ({ theme }: WithTheme) => theme.font.fontFamily[fontFamily]
}

const fontWeight = {
	SBSansText: {
		thin: 250,
		light: 300,
		regular: 400,
		medium: 500,
		semibold: 600,
		bold: 700,
		heavy: 900,
	},
	SBSansInterface: {
		light: 300,
		regular: 400,
		semibold: 600,
		bold: 700,
	},
}
export type FontWeightSBSansText = keyof typeof fontWeight.SBSansText
export type FontWeightSBSansInterface = keyof typeof fontWeight.SBSansInterface

export function themeFontWeight(
	fontFamily: 'SBSansText',
	fontWeight: FontWeightSBSansText
): StyledThemeFn
export function themeFontWeight(
	fontFamily: 'SBSansInterface',
	fontWeight: FontWeightSBSansInterface
): StyledThemeFn
export function themeFontWeight(
	fontFamily: FontFamily,
	fontWeight: FontWeightSBSansText | FontWeightSBSansInterface
) {
	return ({ theme }: WithTheme) => theme.font.fontWeight[fontFamily][fontWeight]
}

export const themeObject = {
	font: {
		fontFamily,
		fontWeight,
	},
	colors: { palette, ...colors },
}

export type Theme = typeof themeObject
export type WithTheme = {
	theme: Theme
}
